import React, { useCallback, useEffect, useMemo } from 'react';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import { connect } from 'react-redux';
import './index.scss';
import FinancialStatement from '$/components/financialStatement';
import { getCompanyFinancials, getDraftCompanyFinancials } from '$/store/portfolio/actions';
import CapitalStructure from '$/components/capitalStructure';
import TransactionDetails from '$/components/transactionDetails';
import classNames from 'classnames';
import DealNotes from '$/components/dealNotes';
import { CLIENT_COMPANY_DEAL_NOTES } from '$/core/constants';
import { getIsCurrentPortfolioCompanyPublicState } from '$/store/portfolio/selectors';

interface ICompanyOverviewProps {
  currentCompany?: Bixby.Models.Company;
  financialStatements: Bixby.Models.FinancialStatement[];
  portfolioIsLoading: boolean;
  showDealNotes: boolean;
  isCurrentPortfolioCompanyPublic: boolean;
  financialsError?: Bixby.Models.BixbyError;
  dispatchGetCompanyFinancial: (
    company: Bixby.Models.Company,
    statementType: Bixby.Enums.FinancialStatementType
  ) => Promise<string[]>;
  dispatchGetDraftCompanyFinancial: (
    company: Bixby.Models.Company,
    statementType: Bixby.Enums.FinancialStatementType
  ) => Promise<string[]>;
}

const CompanyOverview: React.FC<ICompanyOverviewProps> = props => {
  const {
    currentCompany,
    financialStatements,
    dispatchGetCompanyFinancial,
    dispatchGetDraftCompanyFinancial,
    portfolioIsLoading,
    showDealNotes,
    isCurrentPortfolioCompanyPublic,
    financialsError,
  } = props;

  const getFinancials: (currentCompany?: Bixby.Models.Company) => string[][] = (currentCompany): string[][] => {
    let result: string[][] = [];
    if (!currentCompany) {
      return result;
    }

    if (currentCompany.doesNotHaveFinancials) {
      return result;
    }

    const statement = Bixby.Utils.find(financialStatements, item => {
      return (
        item.financialStatementType === Bixby.Enums.FinancialStatementType.SummaryFinancials &&
        item.isDraft === currentCompany.isDraft
      );
    });

    if (statement) {
      result = Object.assign([], statement.data || []);
    }

    return result;
  };

  const statementData = useMemo(() => {
    const statement = Bixby.Utils.find(financialStatements, item => {
      return (
        item.financialStatementType === Bixby.Enums.FinancialStatementType.SummaryFinancials &&
        item.isDraft === currentCompany?.isDraft
      );
    });
    return statement?.data;
  }, [financialStatements, currentCompany]);

  const tryGetFinancials = useCallback(
    (currentCompany: Bixby.Models.Company) => {
      if (!currentCompany) {
        return;
      }

      if (currentCompany.doesNotHaveFinancials) {
        return;
      }

      const dispatchGetFinancials = currentCompany.isDraft
        ? dispatchGetDraftCompanyFinancial
        : dispatchGetCompanyFinancial;

      if (!statementData) {
        dispatchGetFinancials(currentCompany, Bixby.Enums.FinancialStatementType.SummaryFinancials);
      }
    },
    [dispatchGetCompanyFinancial, dispatchGetDraftCompanyFinancial, statementData]
  );

  useEffect(() => {
    if (currentCompany && portfolioIsLoading === false && !financialsError) {
      tryGetFinancials(currentCompany);
    }
  }, [currentCompany, tryGetFinancials, financialsError, portfolioIsLoading]);

  if (Bixby.Utils.isNullOrEmpty(currentCompany)) {
    return null;
  }

  const capitalStructureTable = currentCompany ? <CapitalStructure /> : null;

  const transactionDetailsTable =
    currentCompany && currentCompany.transactionDetails.isVisible ? (
      <TransactionDetails transactionDetails={currentCompany.transactionDetails} />
    ) : null;

  const dealNotesTable = showDealNotes && currentCompany ? <DealNotes currentCompany={currentCompany} /> : null;

  const financialSummaryTable = (
    <div className="green_sheet_wrap">
      <FinancialStatement companyFinancial={getFinancials(currentCompany)} isLoading={portfolioIsLoading} />
    </div>
  );

  return currentCompany ? (
    <React.Fragment>
      <section>
        <div className="company_overview">
          <div
            className={classNames('company_desc_wrap', {
              transaction_details_visible: currentCompany.transactionDetails.isVisible,
              detail_notes_visible: showDealNotes,
            })}>
            {!Bixby.Utils.isNullOrEmpty(currentCompany.announcement) && (
              <div
                className="company_announcement"
                dangerouslySetInnerHTML={{ __html: currentCompany.announcement }}></div>
            )}

            <div className="company_info">
              <div className="info_item">
                <div className="info_item_label">
                  {isCurrentPortfolioCompanyPublic ? 'Investor Relations Site:' : 'Website News:'}
                </div>
                <a href={currentCompany.websiteUrl} target="_blank">
                  {currentCompany.websiteUrl}
                </a>
              </div>
              <div className="info_item">
                <div className="info_item_label">
                  {isCurrentPortfolioCompanyPublic ? 'Public Stock Ticker' : 'Ownership:'}
                </div>
                {isCurrentPortfolioCompanyPublic ? currentCompany.publicStockTicker : currentCompany.ownership}
              </div>
              <div className="info_item">
                <div className="info_item_label">Industry:</div>
                {currentCompany.industry_Name}
              </div>

              <div className="company_desc">{currentCompany.description}</div>
            </div>
            {(transactionDetailsTable || dealNotesTable) && (
              <div className="company_detail_wrap">
                {transactionDetailsTable}
                {dealNotesTable}
              </div>
            )}
          </div>
        </div>

        <div className="company_overview">
          <div className="capital_wrap">{capitalStructureTable}</div>
        </div>

        <div className="company_overview">
          <h2>Financial Summary</h2>
          {financialSummaryTable}
        </div>
      </section>
    </React.Fragment>
  ) : null;
};

const mapStateToProps = (state: AppState) => {
  const { isCurrentPortfolioCompanyPublic } = getIsCurrentPortfolioCompanyPublicState(state);
  return {
    isCurrentPortfolioCompanyPublic,
    currentCompany: state.portfolioState.currentCompany,
    financialStatements: state.portfolioState.financialStatements,
    portfolioIsLoading: state.portfolioState.isLoading,
    showDealNotes: (state.loginState.featureFlags || []).includes(CLIENT_COMPANY_DEAL_NOTES),
    financialsError: state.portfolioState.error,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGetCompanyFinancial: (company: Bixby.Models.Company, statementType: Bixby.Enums.FinancialStatementType) =>
    dispatch(getCompanyFinancials(company, statementType)),
  dispatchGetDraftCompanyFinancial: (
    company: Bixby.Models.Company,
    statementType: Bixby.Enums.FinancialStatementType
  ) => dispatch(getDraftCompanyFinancials(company, statementType)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyOverview);
